/**
 * The four interactive scene objects, drawn programmatically in PixiJS.
 *
 * Each object tells its insurance story through its *art*, not abstract badges:
 *
 *   • house    → neglected (dull, cracked, weeds, grimy windows) when there is
 *                no contract; fresh paint + flower boxes + bright windows once
 *                insured.
 *   • vehicle  → on fire (animated flames + smoke) when uninsured; clean,
 *                brighter, headlights-on once insured.
 *   • travel   → worn luggage when uninsured; vivid + a travel tag once insured.
 *   • person   → the login figure (no contract state).
 *
 * The two art variants are cross-faded by an `insured` value (1 = has a
 * contract in the zone). Only interactive objects animate (the car fire); the
 * backdrop is static apart from the drifting clouds. Every object gets a black
 * pixel-art outline (OutlineFilter); hover is shown by a small lift.
 */
import { Container, Graphics } from 'pixi.js'
import { OutlineFilter } from 'pixi-filters'
import type { ZoneCoverage } from '../zoneState'
import { ZONE_RECTS, type ZoneKey } from '../sceneLayout'
import { PALETTE as P } from './palette'
import { rect, poly, circle } from './draw'

/** Black 1px silhouette outline shared styling for all interactive objects. */
export function makeOutline(): OutlineFilter {
  return new OutlineFilter({ thickness: 1, color: 0x000000, quality: 1 })
}

/**
 * Bombardier Q400 turboprop in side profile, drawn at (ox,oy) scaled by `s`.
 * One shared model so the big parked plane (backdrop) and the fly-away gag
 * plane (character) look identical. `noseRight` mirrors it (so it can fly off
 * tail-first to the left); `gear` toggles the landing gear (down when parked).
 * Base footprint is ~96×37 local units (nose-left, prop on the left).
 */
export function drawDash8(
  g: Graphics,
  ox: number,
  oy: number,
  s: number,
  opts: { noseRight?: boolean; gear?: boolean } = {},
): void {
  const { noseRight = false, gear = true } = opts
  const W = 96
  const X = (x: number) => ox + (noseRight ? W - x : x) * s
  const Y = (y: number) => oy + y * s
  const R = (x: number, y: number, w: number, h: number, c: number, a = 1) =>
    rect(g, ox + (noseRight ? W - x - w : x) * s, oy + y * s, w * s, h * s, c, a)
  const PL = (pts: number[], c: number, a = 1) => {
    const p: number[] = []
    for (let i = 0; i < pts.length; i += 2) p.push(X(pts[i]), Y(pts[i + 1]))
    poly(g, p, c, a)
  }
  const C = (cx: number, cy: number, r: number, c: number, a = 1) =>
    circle(g, X(cx), Y(cy), r * s, c, a)

  if (gear) {
    R(16, 26, 2, 9, P.gearStrut)
    C(17, 35, 2.4, P.gearTyre)
    R(40, 21, 2, 13, P.gearStrut)
    C(37, 35, 2.4, P.gearTyre)
    C(44, 35, 2.4, P.gearTyre)
  }
  // fuselage (nose-left, tail-cone up-right)
  PL([6, 20, 14, 14, 64, 13, 80, 16, 76, 21, 68, 26, 16, 26, 9, 24], P.planeBody)
  R(16, 24, 52, 2, P.planeShade)
  PL([11, 16, 18, 15, 18, 18, 11, 18], P.planeWindow) // cockpit
  for (let x = 22; x < 64; x += 3) R(x, 16, 1, 2, P.planeWindow) // cabin windows
  R(20, 15, 1, 10, P.planeShade) // door line
  // T-tail
  PL([66, 13, 72, 2, 80, 2, 78, 14], P.planeBody) // vertical fin
  PL([70, 3, 90, 1, 90, 3, 72, 5], P.planeBody) // horizontal stabiliser
  R(67, 12, 11, 1, P.planeShade)
  // high wing + forward engine nacelle
  PL([34, 14, 36, 9, 52, 9, 56, 14], P.planeWing)
  PL([22, 14, 50, 14, 50, 20, 24, 21], P.planeShade)
  R(24, 14, 26, 1, P.planeBody)
  // 6-blade turboprop + spinner
  for (let b = 0; b < 6; b++) {
    const a = (b / 6) * Math.PI * 2 + 0.3
    PL(
      [
        22 + Math.cos(a + 0.13) * 2, 12 + Math.sin(a + 0.13) * 2,
        22 + Math.cos(a) * 9, 12 + Math.sin(a) * 9,
        22 + Math.cos(a - 0.13) * 2, 12 + Math.sin(a - 0.13) * 2,
      ],
      P.propBlade,
    )
  }
  C(22, 12, 2.2, P.propHub)
}

export interface SceneObject {
  container: Container
  zone: ZoneKey
  setCoverage(c: ZoneCoverage): void
  setHover(h: boolean): void
  /** True while the character is walking to / acting on this zone (cat hiss). */
  setActive(active: boolean): void
  /** Fades + lifts the uninsured art away (e.g. suitcases carried off by the plane). */
  setGone(gone: boolean): void
  tick(dtMs: number, timeMs: number): void
}

/**
 * Per-frame updater for an object's custom animation (e.g. the car fire, the
 * cat). `activeA` ramps 0→1 while the character is engaging this zone.
 */
type AnimUpdate = (
  timeMs: number,
  uninsuredA: number,
  insuredA: number,
  activeA: number,
) => void

interface ObjectSpec {
  paintUninsured: (g: Graphics) => void
  paintInsured: (g: Graphics) => void
  /**
   * Extra overlay drawn on top of the insured art, visible only when coverage
   * is `'partial'` — i.e. the zone has a contract but a recommendation still
   * flags a gap. Use it to hint at the *kind* of missing cover (e.g. cracked
   * windows on an otherwise shiny car when glass cover is missing).
   */
  paintGap?: (g: Graphics) => void
  /** Adds extra animated layers on top of the art; returns a per-frame fn. */
  setupAnim?: (lift: Container) => AnimUpdate
}

function lerp(cur: number, target: number, dtMs: number, ms = 130): number {
  return cur + (target - cur) * Math.min(1, dtMs / ms)
}

/**
 * Shared scaffolding: a cast shadow, a black silhouette outline, a hover lift,
 * and a cross-fade between the uninsured and insured art variants.
 */
function makeObject(zone: ZoneKey, spec: ObjectSpec): SceneObject {
  const r = ZONE_RECTS[zone]
  const container = new Container()
  container.label = `zone-${zone}`
  container.position.set(r.x, r.y)

  const shadow = new Graphics()
  shadow.ellipse(r.w / 2, r.h - 1, r.w * 0.42, 4).fill({ color: P.shadow, alpha: 0.2 })
  container.addChild(shadow)

  // The art (+ animated overlays) live in `lift`, which carries the black
  // outline so the whole object reads as one stamped silhouette.
  const lift = new Container()
  lift.filters = [makeOutline()]
  container.addChild(lift)

  const gUninsured = new Graphics()
  spec.paintUninsured(gUninsured)
  lift.addChild(gUninsured)

  const gInsured = new Graphics()
  spec.paintInsured(gInsured)
  gInsured.alpha = 0
  lift.addChild(gInsured)

  // "Coverage-gap" overlay on top of the insured art (cracked car windows, etc.).
  // Only visible when coverage is 'partial' AND the insured art is currently
  // showing — so it cross-fades cleanly with the missing→insured transition.
  let gGap: Graphics | null = null
  if (spec.paintGap) {
    gGap = new Graphics()
    spec.paintGap(gGap)
    gGap.alpha = 0
    lift.addChild(gGap)
  }

  // Red "!" marker floating above the zone while no contract exists; fades out
  // as the zone cross-fades to insured. Sits outside `lift` so the outline
  // filter doesn't apply (the badge brings its own dark ring).
  const marker = new Graphics()
  const mr = 6
  circle(marker, 0, 0, mr + 2, 0x000000, 0.35) // soft drop halo
  circle(marker, 0, 0, mr + 1, 0x000000) // dark outline ring
  circle(marker, 0, 0, mr, P.missing) // red disc
  circle(marker, -1.5, -1.5, 2, 0xe87568, 0.6) // highlight
  rect(marker, -1, -4, 2, 5, P.badgeGlyph) // exclamation stem
  rect(marker, -1, 2, 2, 2, P.badgeGlyph) // exclamation dot
  marker.position.set(r.w / 2, -12)
  container.addChild(marker)
  const markerPhase = r.x / 60 // slight per-zone offset so they don't bob in lock-step

  const animUpdate = spec.setupAnim?.(lift)

  let insuredTarget = 0
  let insuredCur = 0
  let gapTarget = 0
  let gapCur = 0
  let hover = false
  let liftCur = 0
  let active = false
  let activeCur = 0
  let gone = false
  let goneCur = 0

  return {
    container,
    zone,
    setCoverage(c) {
      insuredTarget = c === 'missing' ? 0 : 1
      gapTarget = c === 'partial' ? 1 : 0
    },
    setHover(h) {
      hover = h
    },
    setActive(a) {
      active = a
    },
    setGone(g) {
      gone = g
    },
    tick(dtMs, timeMs) {
      insuredCur = lerp(insuredCur, insuredTarget, dtMs, 280)
      gapCur = lerp(gapCur, gapTarget, dtMs, 280)
      liftCur = lerp(liftCur, hover ? 3 : 0, dtMs, 110)
      activeCur = lerp(activeCur, active ? 1 : 0, dtMs, 160)
      goneCur = lerp(goneCur, gone ? 1 : 0, dtMs, 240)

      // `gone` fades + lifts the uninsured art off (carried away).
      gInsured.alpha = insuredCur
      gUninsured.alpha = (1 - insuredCur) * (1 - goneCur)
      gUninsured.y = -goneCur * 22
      shadow.alpha = 0.2 * Math.max(insuredCur, 1 - goneCur)
      lift.y = -liftCur
      // Gap overlay rides the insured art — only visible while insured + 'partial'.
      if (gGap) gGap.alpha = insuredCur * gapCur

      // Missing-contract badge: fully visible while uninsured, hidden once insured,
      // floating gently so the eye catches it.
      marker.alpha = 1 - insuredCur
      marker.y = -12 + Math.sin(timeMs / 400 + markerPhase) * 1.5

      animUpdate?.(timeMs, 1 - insuredCur, insuredCur, activeCur)
    },
  }
}

// ───────────────────────────────────────────────────────────────────────────
// House
// ───────────────────────────────────────────────────────────────────────────
interface HouseScheme {
  wall: number
  wallLight: number
  wallShadow: number
  roof: number
  roofLight: number
  roofShadow: number
  roofRidge: number
  glass: number
  frame: number
  door: number
  doorDark: number
  foundation: number
  foundationDark: number
  chimney: number
  chimneyCap: number
}

const HOUSE_CARED: HouseScheme = {
  wall: P.wall,
  wallLight: P.wallLight,
  wallShadow: P.wallShadow,
  roof: P.roof,
  roofLight: P.roofLight,
  roofShadow: P.roofShadow,
  roofRidge: P.roofRidge,
  glass: P.windowGlass,
  frame: P.windowFrame,
  door: P.doorWood,
  doorDark: P.doorWoodDark,
  foundation: P.foundation,
  foundationDark: P.foundationDark,
  chimney: P.chimney,
  chimneyCap: P.chimneyCap,
}

const HOUSE_NEGLECTED: HouseScheme = {
  wall: P.wallDull,
  wallLight: 0xc2b89e,
  wallShadow: P.wallDullShadow,
  roof: P.roofDull,
  roofLight: P.roofDullLight,
  roofShadow: P.roofDullShadow,
  roofRidge: P.roofDullShadow,
  glass: P.glassGrimy,
  frame: P.wallDullShadow,
  door: P.doorWeathered,
  doorDark: P.doorWeatheredDark,
  foundation: P.foundationDark,
  foundationDark: 0x59503f,
  chimney: P.wallDullShadow,
  chimneyCap: P.foundationDark,
}

function houseShell(g: Graphics, c: HouseScheme): void {
  // Foundation
  rect(g, 10, 104, 98, 8, c.foundationDark)
  rect(g, 10, 104, 98, 2, c.foundation)
  // Wall body
  rect(g, 14, 50, 90, 56, c.wall)
  rect(g, 14, 50, 4, 56, c.wallLight)
  rect(g, 96, 50, 8, 56, c.wallShadow)
  rect(g, 14, 76, 90, 1, c.wallShadow, 0.4)
  // Chimney
  rect(g, 82, 16, 9, 26, c.chimney)
  rect(g, 81, 14, 11, 4, c.chimneyCap)
  rect(g, 88, 16, 3, 26, c.foundationDark, 0.5)
  // Gable roof
  poly(g, [8, 52, 110, 52, 59, 10], c.roof)
  poly(g, [8, 52, 59, 52, 59, 10], c.roofLight)
  poly(g, [110, 52, 59, 52, 59, 10], c.roofShadow)
  for (let i = 1; i <= 4; i++) {
    const y = 10 + i * 8
    const half = ((y - 10) / 42) * 51
    rect(g, Math.round(59 - half), y, Math.round(half * 2), 1, c.roofRidge, 0.35)
  }
  rect(g, 56, 10, 6, 6, c.roofRidge)
  // Eaves overhang — a little shadow lip that reads as a proper roof edge
  rect(g, 6, 49, 106, 1, c.roofLight, 0.5)
  rect(g, 6, 50, 106, 2, c.roofShadow)
  // Attic window centred in the gable — a classic focal detail up top
  rect(g, 52, 29, 14, 14, c.roofShadow)
  rect(g, 53, 30, 12, 12, c.frame)
  rect(g, 55, 32, 8, 8, c.glass)
  rect(g, 58, 32, 1, 8, c.frame)
  rect(g, 55, 35, 8, 1, c.frame)
  // Windows (frame + 4-pane glass + sill)
  const win = (x: number, y: number, w: number, h: number) => {
    rect(g, x - 1, y - 1, w + 2, h + 2, c.frame)
    rect(g, x, y, w, h, c.glass)
    rect(g, x + w / 2 - 0.5, y, 1, h, c.frame)
    rect(g, x, y + h / 2 - 0.5, w, 1, c.frame)
    rect(g, x - 2, y + h + 1, w + 4, 2, c.wallShadow)
  }
  win(24, 58, 18, 18)
  win(64, 58, 18, 18)
  win(24, 84, 16, 16)
  // Shutters flanking the upper windows (painted, louvred)
  for (const ox of [23, 63]) {
    rect(g, ox - 3, 57, 3, 20, c.door)
    rect(g, ox + 20, 57, 3, 20, c.door)
    rect(g, ox - 2, 60, 1, 13, c.doorDark, 0.6)
    rect(g, ox + 21, 60, 1, 13, c.doorDark, 0.6)
  }
  // Door
  rect(g, 62, 80, 22, 26, c.doorDark)
  rect(g, 64, 80, 18, 26, c.door)
  rect(g, 66, 82, 14, 6, c.doorDark)
  rect(g, 78, 92, 2, 3, P.doorHandle)
  rect(g, 60, 104, 26, 3, c.foundation)
  // Entry porch awning over the door + a stone step out front
  poly(g, [60, 81, 86, 81, 82, 77, 64, 77], c.roof)
  rect(g, 60, 80, 26, 1, c.roofShadow)
  rect(g, 56, 107, 34, 3, c.foundationDark)
  rect(g, 58, 105, 30, 2, c.foundation)
}

/** A low front-garden fence with a gate gap at the door. */
function gardenFence(g: Graphics, cared: boolean): void {
  const yTop = 103
  const railC = cared ? 0xd9cdb4 : 0x7d7563
  const pickC = cared ? P.windowFrame : 0x968c79
  const run = (x0: number, x1: number) => {
    rect(g, x0, yTop + 3, x1 - x0, 1, railC) // top rail
    rect(g, x0, yTop + 7, x1 - x0, 1, railC) // bottom rail
    let n = 0
    for (let px = x0; px <= x1 - 2; px += 5, n++) {
      if (!cared && n % 4 === 2) continue // a picket snapped clean off
      const lean = !cared && n % 3 === 0 ? 1 : 0
      rect(g, px + lean, yTop, 2, 10, pickC)
      rect(g, px + lean, yTop - 1, 2, 1, pickC) // pointed cap
      if (!cared) rect(g, px + lean, yTop + 4, 2, 1, P.stain, 0.4)
    }
  }
  run(10, 58) // left of the gate
  run(88, 108) // right of the gate
}

/** A window flower box with three little blooms. */
function flowerBox(g: Graphics, x: number, w: number, y: number): void {
  rect(g, x - 2, y, w + 4, 4, P.flowerBox)
  rect(g, x - 2, y, w + 4, 1, 0x83592f)
  const blooms = [P.flowerRed, P.flowerYellow, P.flowerPink]
  let bi = 0
  for (let bx = x; bx < x + w; bx += 6) {
    rect(g, bx + 1, y - 2, 1, 2, P.flowerLeaf)
    rect(g, bx, y - 4, 3, 3, blooms[bi % blooms.length])
    rect(g, bx + 1, y - 3, 1, 1, P.flowerYellow)
    bi++
  }
}

function paintHouseInsured(g: Graphics): void {
  houseShell(g, HOUSE_CARED)
  // glass glints
  for (const [x, y] of [[24, 58], [64, 58], [24, 84]] as const) {
    rect(g, x + 1, y + 1, 3, 3, 0xc8e0ec)
  }
  // window flower boxes
  flowerBox(g, 24, 18, 76)
  flowerBox(g, 64, 18, 76)
  flowerBox(g, 24, 16, 100)
  // door planters
  for (const px of [54, 86]) {
    rect(g, px, 99, 5, 6, P.flowerBox)
    rect(g, px + 1, 96, 1, 3, P.flowerLeaf)
    rect(g, px, 94, 3, 3, P.flowerRed)
  }
  gardenFence(g, true) // tidy white picket fence
}

function paintHouseNeglected(g: Graphics): void {
  houseShell(g, HOUSE_NEGLECTED)
  // wall cracks (thin dark zig-zags)
  poly(g, [46, 52, 47, 64, 44, 72, 46, 82], P.foundationDark, 0.45)
  poly(g, [92, 60, 90, 70, 92, 80], P.foundationDark, 0.4)
  // damp stains streaking from window sills
  rect(g, 26, 77, 3, 14, P.stain, 0.3)
  rect(g, 67, 77, 3, 14, P.stain, 0.3)
  // boarded-up lower-left window
  rect(g, 22, 87, 20, 3, P.doorWeatheredDark)
  rect(g, 22, 96, 20, 3, P.doorWeatheredDark)
  poly(g, [22, 99, 26, 99, 42, 85, 38, 85], P.doorWeathered, 0.9)
  // missing roof tiles (dark gaps)
  rect(g, 44, 30, 4, 3, P.roofDullShadow)
  rect(g, 68, 38, 5, 3, P.roofDullShadow)
  rect(g, 54, 22, 3, 2, P.roofDullShadow)
  // weeds along the base
  for (let x = 14; x < 104; x += 7) {
    const h = 4 + ((x * 7) % 4)
    rect(g, x, 104 - h, 1, h, P.weedDark)
    rect(g, x + 1, 106 - h, 1, h - 2, P.weed)
    rect(g, x - 1, 105 - h, 1, h - 3, P.weed)
  }
  gardenFence(g, false) // grey, leaning, gap-toothed fence
}

// ───────────────────────────────────────────────────────────────────────────
// Car
// ───────────────────────────────────────────────────────────────────────────
interface CarScheme {
  body: number
  bodyLight: number
  bodyDark: number
  glass: number
  glassDark: number
  trim: number
  headlight: number
  taillight: number
}

const CAR_SHINY: CarScheme = {
  body: P.carBody,
  bodyLight: P.carBodyLight,
  bodyDark: P.carBodyDark,
  glass: P.carGlass,
  glassDark: P.carGlassDark,
  trim: P.carTrim,
  headlight: P.carHeadlight,
  taillight: P.carTaillight,
}

const CAR_DULL: CarScheme = {
  body: P.carDull,
  bodyLight: P.carDullLight,
  bodyDark: P.carDullDark,
  glass: 0x59666f,
  glassDark: 0x424c54,
  trim: 0x7a8088,
  headlight: P.headlightOff,
  taillight: P.taillightOff,
}

// Škoda Octavia (Mk4 liftback) — sleek red fastback sedan, facing right.
function carBase(g: Graphics, c: CarScheme): void {
  // ─── Lower body ──────────────────────────────────────────────────────────
  poly(
    g,
    [3, 30, 4, 23, 12, 20, 40, 19, 60, 20, 84, 21, 96, 23, 99, 27, 99, 31, 96, 34, 5, 34],
    c.body,
  )
  // shoulder sheen + the Octavia's strong character line + rocker shadow
  poly(g, [12, 21, 40, 20, 84, 22, 84, 23, 40, 21, 12, 22], c.bodyLight)
  rect(g, 8, 26, 84, 1, c.bodyLight, 0.45)
  rect(g, 8, 27, 84, 1, c.bodyDark, 0.5)
  rect(g, 6, 31, 90, 1, c.bodyDark, 0.4)
  rect(g, 6, 33, 90, 1, c.bodyDark)

  // ─── Ducktail spoiler (rear lip) ─────────────────────────────────────────
  poly(g, [9, 20, 10, 17, 18, 18, 18, 20], c.body)
  rect(g, 9, 20, 10, 1, c.bodyDark, 0.5)

  // ─── Greenhouse (fastback) ───────────────────────────────────────────────
  poly(g, [60, 20, 52, 9, 43, 9, 13, 19], c.body)
  poly(g, [57, 18, 54, 11, 44, 11, 17, 18], c.glass) // continuous side glazing
  poly(g, [54, 11, 44, 11, 18, 17, 22, 17, 47, 12], P.glassSheen, 0.3) // sheen
  rect(g, 44, 11, 2, 8, 0x1c1c20) // blacked-out B-pillar
  rect(g, 16, 18, 41, 1, c.trim, 0.7) // chrome belt-line
  poly(g, [52, 9, 43, 9, 43, 10, 52, 10], c.trim, 0.6) // chrome roof rail

  // ─── Lights ──────────────────────────────────────────────────────────────
  poly(g, [3, 22, 11, 23, 11, 25, 3, 26], c.taillight) // slim LED taillight
  rect(g, 4, 22, 6, 1, 0xf28a78, 0.6)
  poly(g, [94, 24, 99, 25, 99, 27, 93, 26], c.headlight) // slim LED headlight
  rect(g, 92, 30, 7, 2, P.carGrille) // lower intake
  rect(g, 95, 28, 4, 1, P.carGrille, 0.6)
  rect(g, 95, 30, 4, 4, c.bodyDark, 0.35) // front bumper shade

  // ─── Details ─────────────────────────────────────────────────────────────
  rect(g, 40, 23, 5, 1, c.trim) // front door handle
  rect(g, 22, 23, 5, 1, c.trim) // rear door handle
  rect(g, 46, 20, 1, 12, c.bodyDark, 0.4) // B-pillar door seam
  rect(g, 31, 20, 1, 12, c.bodyDark, 0.3) // rear door seam
  rect(g, 58, 19, 3, 2, c.bodyDark) // door mirror

  // ─── Large multi-spoke alloys + red brake caliper (RS) ───────────────────
  for (const cx of [23, 79]) {
    circle(g, cx, 32, 10, P.carArch) // arch opening
    circle(g, cx, 32, 9, P.carTire) // tyre
    circle(g, cx, 32, 7.5, P.carTyreInner)
    circle(g, cx, 32, 6.4, P.carRim) // silver outer lip
    circle(g, cx, 32, 5.6, P.carRimFace) // dark alloy face
    circle(g, cx + 2, 34, 1.4, P.carCaliper) // red caliper
    for (let s = 0; s < 10; s++) {
      const a = (s / 10) * Math.PI * 2
      circle(g, cx + Math.cos(a) * 3.9, 32 + Math.sin(a) * 3.9, 0.9, P.carRim)
    }
    circle(g, cx, 32, 1.8, P.carRimDark) // hub
  }
}

function paintCarInsured(g: Graphics): void {
  carBase(g, CAR_SHINY)
  // headlight beam + glow (lights on; front = right)
  poly(g, [99, 24, 122, 20, 123, 32], P.beam, 0.16)
  poly(g, [99, 25, 117, 23, 118, 30], P.beam, 0.22)
  circle(g, 96, 26, 2, P.carHeadlight, 0.95)
  circle(g, 96, 26, 1, 0xffffff, 0.95)
  circle(g, 5, 24, 1.8, P.carTaillight, 0.85) // taillight glow
}

function paintCarUninsured(g: Graphics): void {
  carBase(g, CAR_DULL)
  // soot smudges where the flames lick the bodywork
  rect(g, 30, 19, 30, 3, P.smoke, 0.4)
  rect(g, 64, 21, 18, 2, P.smoke, 0.3)
}

/**
 * Coverage-gap overlay for the car (shown when `vehicle` is `'partial'` — i.e.
 * has MTPL but is missing glass / casco cover). Draws a small impact mark on
 * the side glazing with cracks radiating outward, kept inside the greenhouse
 * area so it reads as broken glass and not random scratches on the paint.
 */
function paintCarCracks(g: Graphics): void {
  const crack = 0x1a1a1f
  const cx = 33
  const cy = 14
  // Pixel-stepped line from (cx,cy) outward by (dx,dy).
  const line = (dx: number, dy: number) => {
    const steps = Math.max(Math.abs(dx), Math.abs(dy))
    for (let s = 1; s <= steps; s++) {
      const x = cx + Math.round((dx * s) / steps)
      const y = cy + Math.round((dy * s) / steps)
      rect(g, x, y, 1, 1, crack)
    }
  }
  // Impact spot
  rect(g, cx - 1, cy - 1, 2, 2, crack)
  rect(g, cx, cy, 1, 1, 0x0a0a0c)
  // Radiating cracks (kept within the side-glazing trapezoid roughly x18..56, y11..18)
  line(-9, -3)
  line(-5, 3)
  line(-3, -3)
  line(4, -3)
  line(7, 2)
  line(11, -1)
  // A short branch off the upper-right crack for a touch of irregularity
  rect(g, cx + 5, cy - 2, 1, 1, crack)
  rect(g, cx + 6, cy - 2, 1, 1, crack)
}

/**
 * Animated flames + smoke for the burning (uninsured) car. `getDouse` is a
 * 0..1 multiplier injected from outside (the fire truck while it sprays):
 * 0 = fire untouched, 1 = fire fully suppressed. The flames shrink with douse
 * so the dousing is visibly effective.
 */
function setupCarFire(lift: Container, getDouse: () => number = () => 0): AnimUpdate {
  const smoke = new Graphics()
  const flame = new Graphics()
  lift.addChild(smoke)
  lift.addChild(flame)

  const tongues = [12, 26, 40, 54, 68, 82]

  function tongue(g: Graphics, x: number, baseY: number, h: number) {
    poly(g, [x - 5, baseY, x + 5, baseY, x, baseY - h], P.fireDeep)
    poly(g, [x - 3, baseY, x + 3, baseY, x, baseY - Math.round(h * 0.78)], P.fireMid)
    poly(g, [x - 1.5, baseY, x + 1.5, baseY, x, baseY - Math.round(h * 0.5)], P.fireHot)
  }

  return (timeMs, uninsuredA) => {
    const douse = Math.max(0, Math.min(1, getDouse()))
    const fireA = uninsuredA * (1 - douse)
    flame.alpha = fireA
    // Smoke lingers slightly longer than flame as the fire dies.
    smoke.alpha = Math.max(fireA, uninsuredA * (1 - douse * 0.6))
    if (fireA < 0.02) {
      flame.clear()
      smoke.clear()
      return
    }

    flame.clear()
    const shrink = 1 - douse * 0.85
    for (let i = 0; i < tongues.length; i++) {
      const phase = timeMs / 110 + i * 1.3
      const raw = 13 + Math.sin(phase) * 5 + Math.sin(phase * 2.3) * 3
      const h = Math.max(3, Math.round(raw * shrink))
      tongue(flame, tongues[i], 20, h)
    }
    // a couple of detached embers (also dampened by dousing)
    if (douse < 0.7) {
      const ey = 4 + ((timeMs / 90) % 10)
      circle(flame, 50, Math.round(2 - ey), 1, P.ember, 0.9 * (1 - douse))
      circle(flame, 70, Math.round(6 - ((timeMs / 70) % 12)), 1, P.fireHot, 0.9 * (1 - douse))
    }

    smoke.clear()
    for (let i = 0; i < 3; i++) {
      const t = ((timeMs / 1500 + i * 0.4) % 1)
      const sy = 2 - t * 42
      const sx = 40 + i * 14 + Math.sin(timeMs / 700 + i) * 3
      const a = 0.3 * (1 - t)
      rect(smoke, Math.round(sx), Math.round(sy), 8, 5, P.smoke, a)
      rect(smoke, Math.round(sx) + 4, Math.round(sy) - 3, 6, 4, P.smoke, a * 0.8)
    }
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Suitcases
// ───────────────────────────────────────────────────────────────────────────
interface CaseScheme {
  teal: number
  tealHi: number
  tealDark: number
  tan: number
  tanHi: number
  tanDark: number
  plum: number
  plumHi: number
  plumDark: number
}

const CASE_VIVID: CaseScheme = {
  teal: P.caseTeal,
  tealHi: 0x3f7c80,
  tealDark: P.caseTealDark,
  tan: P.caseTan,
  tanHi: 0xc4743f,
  tanDark: P.caseTanDark,
  plum: P.casePlum,
  plumHi: 0x83507a,
  plumDark: P.casePlumDark,
}

const CASE_WORN: CaseScheme = {
  teal: P.caseTealDull,
  tealHi: 0x546a6c,
  tealDark: 0x36474a,
  tan: P.caseTanDull,
  tanHi: 0x9c785c,
  tanDark: 0x6c4f3a,
  plum: P.casePlumDull,
  plumHi: 0x6b5a66,
  plumDark: 0x453845,
}

function casesBase(g: Graphics, c: CaseScheme): void {
  // Tall teal hardshell (back-left)
  rect(g, 6, 9, 24, 31, c.teal)
  rect(g, 6, 9, 3, 31, c.tealHi)
  rect(g, 26, 9, 4, 31, c.tealDark)
  for (let x = 11; x < 28; x += 4) rect(g, x, 11, 1, 27, c.tealDark, 0.6)
  rect(g, 13, 5, 10, 4, P.caseStrap)
  rect(g, 15, 2, 6, 4, P.caseStrap)
  rect(g, 16, 3, 4, 2, c.teal)
  rect(g, 7, 22, 22, 1, c.tealDark)
  // Tan leather case (front-right)
  rect(g, 28, 19, 24, 21, c.tan)
  rect(g, 28, 19, 3, 21, c.tanHi)
  rect(g, 48, 19, 4, 21, c.tanDark)
  rect(g, 28, 27, 24, 3, P.caseStrap)
  rect(g, 37, 27, 4, 3, P.caseBuckle)
  rect(g, 35, 15, 10, 4, c.tanDark)
  rect(g, 37, 12, 6, 3, P.caseStrap)
  // Small plum case (front-left)
  rect(g, 1, 30, 16, 10, c.plum)
  rect(g, 1, 30, 2, 10, c.plumHi)
  rect(g, 13, 30, 4, 10, c.plumDark)
  rect(g, 5, 27, 8, 3, P.caseStrap)
  rect(g, 1, 34, 16, 1, c.plumDark)
}

function paintSuitcasesInsured(g: Graphics): void {
  // (The aircraft now lives in the backdrop — a Q400 parked on a runway, set
  // back in the background — so the suitcases just get stamps + a travel tag.)
  casesBase(g, CASE_VIVID)
  // travel tag hanging from the teal handle
  rect(g, 18, 6, 1, 5, P.tagString)
  rect(g, 16, 11, 5, 4, P.tag)
  rect(g, 18, 12, 1, 2, P.flowerBox)
  // a round destination sticker on the tan case
  circle(g, 44, 23, 2.5, P.flowerRed, 0.95)
  circle(g, 44, 23, 1, P.flowerYellow, 0.95)
  // travel stamps on the cases
  rect(g, 10, 14, 4, 3, P.stampBlue, 0.85)
  rect(g, 21, 24, 3, 4, P.stampRed, 0.85)
  rect(g, 31, 32, 4, 3, P.stampGreen, 0.85)
  circle(g, 46, 34, 1.6, P.stampBlue, 0.85)
}

function paintSuitcasesUninsured(g: Graphics): void {
  casesBase(g, CASE_WORN)
  // scuff on the teal case + a strip of packing tape on the tan one
  rect(g, 9, 29, 7, 2, P.smoke, 0.4)
  rect(g, 33, 24, 7, 2, 0xcfc3a0, 0.7)
}

// ───────────────────────────────────────────────────────────────────────────
// Life zone — Reaper (uninsured: memento mori) ↔ Angel (insured: protected).
// Drawn in a 38×66 box, feet on the ground at y66.
// ───────────────────────────────────────────────────────────────────────────
function paintReaper(g: Graphics): void {
  // Hooded robe
  poly(g, [12, 20, 26, 20, 34, 64, 4, 64], P.reaperRobe)
  poly(g, [26, 20, 34, 64, 20, 64, 19, 20], P.reaperRobeDark) // right shade
  rect(g, 13, 26, 1, 36, P.reaperRobeDark, 0.5) // folds
  rect(g, 22, 24, 1, 38, P.reaperRobeDark, 0.5)
  // Hood
  poly(g, [10, 22, 12, 8, 19, 3, 26, 8, 28, 22], P.reaperRobe)
  poly(g, [19, 3, 26, 8, 28, 22, 22, 22, 21, 7], P.reaperRobeDark)
  // Face void + glowing eyes
  poly(g, [15, 10, 23, 10, 22, 20, 16, 20], P.reaperHood)
  rect(g, 16, 13, 2, 2, P.reaperEye)
  rect(g, 20, 13, 2, 2, P.reaperEye)
  // Skeletal hands
  rect(g, 8, 40, 4, 4, P.bone)
  rect(g, 26, 38, 5, 4, P.bone)
  // Scythe: pole + hooked blade
  poly(g, [27, 41, 30, 41, 36, 2, 33, 2], P.scythePole)
  poly(g, [35, 2, 17, 2, 24, 9], P.scytheBlade)
  rect(g, 17, 1, 18, 1, P.scytheBladeDark)
}

function paintAngel(g: Graphics): void {
  // Wings (behind the body)
  poly(g, [12, 24, 1, 27, 3, 43, 14, 37], P.angelWing)
  poly(g, [26, 24, 37, 27, 35, 43, 24, 37], P.angelWing)
  rect(g, 4, 30, 1, 9, P.angelWingShade, 0.6)
  rect(g, 33, 30, 1, 9, P.angelWingShade, 0.6)
  // Robe + sleeves
  poly(g, [13, 22, 25, 22, 30, 64, 8, 64], P.angelRobe)
  poly(g, [25, 22, 30, 64, 20, 64, 19, 22], P.angelRobeShade)
  rect(g, 18, 24, 1, 38, P.angelRobeShade, 0.4)
  rect(g, 9, 24, 4, 18, P.angelRobe)
  rect(g, 25, 24, 4, 18, P.angelRobe)
  rect(g, 9, 40, 4, 3, P.skin) // hands
  rect(g, 25, 40, 4, 3, P.skin)
  // Head + fair hair
  rect(g, 15, 9, 8, 9, P.skin)
  rect(g, 22, 10, 1, 8, P.skinShadow)
  rect(g, 14, 7, 10, 4, 0xddc08a)
  rect(g, 14, 9, 2, 4, 0xddc08a)
  rect(g, 22, 9, 2, 3, 0xddc08a)
  // Halo
  g.ellipse(19, 4, 6, 2).stroke({ width: 1.5, color: P.angelHalo })
}

// ───────────────────────────────────────────────────────────────────────────
// Pet zone — cat up a tree (uninsured, hisses on approach) ↔ cat asleep in a
// cradle (insured). Drawn in a 46×108 box, tree base on the ground at y108.
// The tree is shared by both variants; the live perched cat is an animated
// overlay (setupPetCat) so it can switch sitting → hissing when the character
// arrives.
// ───────────────────────────────────────────────────────────────────────────
function drawPetTree(g: Graphics): void {
  // branch stub (left) the cat perches on
  rect(g, 7, 50, 16, 3, P.petTrunk)
  rect(g, 7, 52, 16, 1, P.petTrunkDark)
  // trunk
  rect(g, 19, 50, 9, 56, P.petTrunk)
  rect(g, 25, 50, 3, 56, P.petTrunkDark)
  rect(g, 19, 62, 9, 1, P.petTrunkDark, 0.5)
  rect(g, 19, 82, 9, 1, P.petTrunkDark, 0.5)
  // root flare
  rect(g, 15, 102, 18, 4, P.petTrunk)
  rect(g, 15, 104, 18, 2, P.petTrunkDark)
  // foliage canopy (layered blobs)
  circle(g, 23, 30, 21, P.petFoliageDark)
  circle(g, 13, 28, 12, P.petFoliage)
  circle(g, 32, 25, 13, P.petFoliage)
  circle(g, 23, 16, 13, P.petFoliage)
  circle(g, 16, 18, 6, P.petFoliageLight)
  circle(g, 29, 15, 5, P.petFoliageLight)
}

function paintPetUninsured(g: Graphics): void {
  drawPetTree(g)
}

function paintPetInsured(g: Graphics): void {
  drawPetTree(g)
  // cradle at the tree base
  poly(g, [5, 102, 31, 102, 29, 106, 7, 106], P.cradleWoodDark) // rockers
  rect(g, 6, 93, 25, 10, P.cradleWood)
  rect(g, 6, 93, 25, 2, 0x9a774e)
  rect(g, 27, 93, 4, 10, P.cradleWoodDark)
  // blanket
  rect(g, 8, 90, 21, 5, P.cradleBlanket)
  rect(g, 8, 93, 21, 2, P.cradleBlanketShade)
  // sleeping cat curled on the blanket
  rect(g, 11, 85, 15, 6, P.catFur)
  rect(g, 11, 89, 15, 2, P.catFurDark)
  rect(g, 22, 85, 5, 5, P.catFur) // head
  poly(g, [22, 85, 24, 82, 25, 85], P.catFur) // ear
  rect(g, 23, 87, 3, 1, P.catFurDark) // closed eye
  rect(g, 12, 86, 3, 4, P.catFurDark) // tail wrap
  // "zzz"
  rect(g, 28, 80, 3, 1, P.hissMark)
  rect(g, 29, 81, 1, 1, P.hissMark)
  rect(g, 28, 82, 3, 1, P.hissMark)
  rect(g, 31, 75, 2, 1, P.hissMark)
  rect(g, 31, 77, 2, 1, P.hissMark)
}

/** The live perched cat: sits calmly, then arches + hisses when active. */
function setupPetCat(lift: Container): AnimUpdate {
  const cat = new Graphics()
  lift.addChild(cat)
  return (timeMs, uninsuredA, _insuredA, activeA) => {
    cat.alpha = uninsuredA
    cat.clear()
    if (uninsuredA < 0.02) return

    if (activeA > 0.35) {
      // hissing — arched back, puffed tail, open mouth, facing left
      rect(cat, 11, 44, 2, 6, P.catFur) // legs
      rect(cat, 18, 44, 2, 6, P.catFur)
      poly(cat, [10, 44, 12, 34, 20, 33, 22, 44], P.catFur) // arched back
      rect(cat, 10, 42, 12, 4, P.catFur)
      rect(cat, 11, 35, 9, 2, P.catFurDark, 0.5)
      rect(cat, 20, 30, 4, 15, P.catFur) // puffed tail
      rect(cat, 20, 30, 2, 15, P.catFurDark, 0.4)
      rect(cat, 5, 40, 8, 6, P.catFur) // head forward (left)
      poly(cat, [9, 40, 12, 37, 13, 41], P.catFur) // ear back
      rect(cat, 5, 43, 3, 2, P.catMouth) // open mouth
      rect(cat, 6, 44, 1, 1, 0xffffff) // fang
      rect(cat, 8, 41, 2, 1, P.catPupil) // angry eye
      const f = 0.35 + 0.65 * Math.abs(Math.sin(timeMs / 90))
      poly(cat, [4, 39, 0, 37, 2, 40], P.hissMark, f) // flicking hiss marks
      poly(cat, [4, 43, -1, 43, 2, 45], P.hissMark, f)
      poly(cat, [4, 47, 0, 49, 2, 47], P.hissMark, f)
    } else {
      // sitting calmly, facing left
      rect(cat, 12, 42, 9, 8, P.catFur) // haunches
      rect(cat, 9, 44, 4, 6, P.catFur) // chest
      rect(cat, 10, 47, 3, 3, P.catBelly) // belly
      rect(cat, 19, 40, 2, 10, P.catFur) // tail
      rect(cat, 18, 38, 3, 2, P.catFur)
      rect(cat, 7, 38, 7, 6, P.catFur) // head
      poly(cat, [7, 38, 9, 35, 11, 38], P.catFur) // ear
      poly(cat, [11, 38, 13, 35, 14, 38], P.catFur) // ear
      rect(cat, 8, 36, 1, 1, P.catEar)
      rect(cat, 8, 40, 1, 1, P.catEye) // eye
      rect(cat, 14, 45, 6, 1, P.catStripe, 0.5) // stripe
    }
  }
}

// ───────────────────────────────────────────────────────────────────────────
// The "person" zone is the main character (see character.ts), not a static
// object — so there is no buildPerson here.
export function buildHouse(): SceneObject {
  return makeObject('house', {
    paintUninsured: paintHouseNeglected,
    paintInsured: paintHouseInsured,
  })
}
/** The car's SceneObject, extended with a `setDousing` knob the fire truck drives. */
export interface VehicleSceneObject extends SceneObject {
  /** 0 = flames full, 1 = flames fully suppressed. Smoothed inside `tick`. */
  setDousing(amount: number): void
}

export function buildCar(): VehicleSceneObject {
  let douseTarget = 0
  let douseCur = 0
  const obj = makeObject('vehicle', {
    paintUninsured: paintCarUninsured,
    paintInsured: paintCarInsured,
    paintGap: paintCarCracks,
    setupAnim: (lift) => setupCarFire(lift, () => douseCur),
  })
  // `setupAnim` runs once at build time; we lerp `douseCur` here on every tick
  // (animUpdate doesn't get dtMs).
  const innerTick = obj.tick
  obj.tick = (dtMs, timeMs) => {
    douseCur += (douseTarget - douseCur) * Math.min(1, dtMs / 240)
    innerTick(dtMs, timeMs)
  }
  return Object.assign(obj, {
    setDousing(amount: number) {
      douseTarget = Math.max(0, Math.min(1, amount))
    },
  })
}
export function buildSuitcases(): SceneObject {
  return makeObject('travel', {
    paintUninsured: paintSuitcasesUninsured,
    paintInsured: paintSuitcasesInsured,
  })
}
export function buildPet(): SceneObject {
  return makeObject('pet', {
    paintUninsured: paintPetUninsured, // no pet contract → cat up the tree
    paintInsured: paintPetInsured, // pet contract → cat asleep in a cradle
    setupAnim: setupPetCat,
  })
}
export function buildLife(): SceneObject {
  return makeObject('life', {
    paintUninsured: paintReaper, // no life contract → the Reaper looms
    paintInsured: paintAngel, // life contract → a guardian Angel
  })
}
